/**
 * Gemini Live clinical interpreter: system instruction + tool schema (non-React).
 * Used by ChartMind Live interpreter hook; reusable for other bilingual voice flows.
 */

import { Type } from '@google/genai';
import { getLanguageName } from '@healthdesk/shared-services';

/** Tool name — model must call after each interpreted turn to log structured chart data */
export const LOG_INTERPRETER_TURN_TOOL_NAME = 'log_interpreter_turn';

/**
 * @returns {import('@google/genai').FunctionDeclaration}
 */
export const buildLogInterpreterTurnToolDeclaration = () => ({
  name: LOG_INTERPRETER_TURN_TOOL_NAME,
  description:
    'Call after you interpret a spoken turn: log who spoke, the original words, what you said aloud ' +
    'for the other party, and the provider-language line for the medical chart. ' +
    'originalText must reflect only what was actually said—no added symptoms, history, or details. ' +
    'translatedForOtherParty and normalizedForChart must be faithful translations only; do not invent clinical facts or complete missing speech.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      speaker: {
        type: Type.STRING,
        description: 'Who just spoke in the room: exactly "provider" or "patient".',
        enum: ['provider', 'patient'],
      },
      originalText: {
        type: Type.STRING,
        description: 'What they said, in the language they used.',
      },
      translatedForOtherParty: {
        type: Type.STRING,
        description: 'What you spoke (or will speak) aloud so the other party understands, in their language.',
      },
      normalizedForChart: {
        type: Type.STRING,
        description:
          'Same clinical content in the provider working language for the chart (concise, no extra commentary).',
      },
    },
    required: ['speaker', 'originalText', 'translatedForOtherParty', 'normalizedForChart'],
  },
});

/**
 * @param {Object} opts
 * @param {string} opts.providerLanguage - BCP-47-ish code
 * @param {string} opts.patientLanguage
 * @param {string} [opts.glossaryAppendix] - Optional bullet lines from ChartMind glossary
 * @param {string} [opts.currentSpeakerHint] - Optional UI hint: "provider" | "patient"
 * @returns {string}
 */
export const buildClinicalInterpreterSystemInstruction = ({
  providerLanguage,
  patientLanguage,
  glossaryAppendix = '',
  currentSpeakerHint = '',
}) => {
  const pl = getLanguageName(providerLanguage) || providerLanguage;
  const pt = getLanguageName(patientLanguage) || patientLanguage;

  let s =
    'You are a clinical dialogue interpreter in a medical encounter. ' +
    'You do not diagnose, treat, or document beyond faithful interpretation. ' +
    'Never invent symptoms, medications, doses, findings, or dialogue the speaker did not say. ' +
    'Do not complete partial utterances with guessed content; translate only what was actually spoken. ' +
    `Provider working (chart) language is ${pl} (${providerLanguage}). ` +
    `Patient is using ${pt} (${patientLanguage}). ` +
    'Listen to live audio; speak short, natural interpretations so each party understands the other. ' +
    'After each interpreted turn you must call ' +
    LOG_INTERPRETER_TURN_TOOL_NAME +
    ' exactly once with structured fields. ' +
    'If audio is unclear, say so briefly and still log your best-effort text in originalText without fabricating specifics. ';

  if (currentSpeakerHint === 'provider' || currentSpeakerHint === 'patient') {
    s += `The ChartMind UI currently marks the active speaker as: ${currentSpeakerHint}. `;
    s += 'Use what you hear to set the tool `speaker` field; the UI hint may disagree if the room order differs. ';
  }

  if (glossaryAppendix && glossaryAppendix.trim()) {
    s += `\n\nPreferred terminology when applicable:\n${glossaryAppendix.trim()}\n`;
  }

  return s;
};

/**
 * @param {Object} opts
 * @param {string} opts.providerLanguage
 * @param {string} opts.patientLanguage
 * @param {Array<{source: string, target: string}>} [opts.glossaryEntries]
 * @returns {string} Appendix for system instruction
 */
export const formatGlossaryAppendix = ({ glossaryEntries = [] }) => {
  if (!glossaryEntries.length) return '';
  return glossaryEntries.map((e) => `- "${e.source}" → "${e.target}"`).join('\n');
};
